import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';

import { Asset } from 'entities/assets/interfaces/asset';
import { AssetType } from 'entities/assets/model';
import { ImageReview } from 'entities/assets/ui/image-review';
import { FilterPicker } from 'features/asset/filter-picker/filter-picker';
import { KeywordsPicker } from 'features/asset/keywords-picker';
import {
	updateBackgroundSchema,
	UpdateBackgroundValues,
} from 'features/asset/update/lib/schemas/background';
import {
	GetAssetByIdDocument,
	GetAssetsDocument,
} from 'pages/art-catalog/ui/ArtCatalogTable/queries.gen';
import { useGetAssetError } from 'shared/hooks/asset-error';
import { useAppToast } from 'shared/hooks/toast';
import { InputField } from 'shared/ui';
import { InputFieldWithLink } from 'shared/ui/input-field-with-link';
import { GetBackgroundDocument } from 'widgets/update-asset-modal/ui/modal/queries.gen';

import { useUpdateBackgroundMutation } from './graphql/queries.gen';

export interface Props {
	asset: Asset;
}

export const UpdateBackgroundForm: React.FC<Props> = ({ asset }) => {
	const toast = useAppToast();
	const handleError = useGetAssetError();
	const [isDisabledFilename, setIsDisabledFilename] = useState(
		!!asset.filename,
	);
	const [isUploadError, setIsUploadError] = useState(false);

	useEffect(() => {
		setIsDisabledFilename(!!asset.filename);
	}, [asset.filename]);

	const [updateBackground] = useUpdateBackgroundMutation({
		onCompleted: () => {
			toast({
				title: 'Background updated successfully',
				status: 'success',
			});
			setIsUploadError(false);
		},
		onError: (mutationError) => {
			handleError(mutationError.message, 'Background');
			setIsUploadError(!asset?.link);
		},
		refetchQueries: [
			GetAssetsDocument,
			GetBackgroundDocument,
			GetAssetByIdDocument,
		],
	});

	return (
		<Formik<UpdateBackgroundValues>
			initialValues={{
				id: asset.id,
				filename: asset.filename,
				link: asset.link,
				keywords: asset.keywords,
				newKeywords: asset.newKeywords || {},
				visionLabels: asset.visionLabels,
				filter: asset.filter,
				dropboxLink: asset.dropboxLink || '',
			}}
			validationSchema={updateBackgroundSchema}
			onSubmit={(values) =>
				updateBackground({ variables: { background: values } })
			}
			enableReinitialize
		>
			{({ values, submitForm, setFieldValue, errors, validateForm }) => (
				<Form>
					<InputField
						label="Filename"
						name="filename"
						value={values.filename?.trim() || ''}
						onApply={async (v) => {
							setFieldValue('filename', v, true);
							await validateForm();
							if (!errors.filename) submitForm();
						}}
						error={errors.filename}
						placeholder="ext-background-name-day.jpg"
						isDisabledFilename={isDisabledFilename}
					/>
					<ImageReview
						label="Background"
						type={asset.type}
						atName={isUploadError ? '' : asset?.atName?.trim() || ''}
						imageSrc={isUploadError ? '' : values.link || ''}
						onImageChange={async (link) => {
							setFieldValue('link', link, true);
							await validateForm();
							return submitForm();
						}}
						isUploadError={isUploadError}
						error={errors.link}
					/>
					<KeywordsPicker
						type={AssetType.BACKGROUND}
						selectedKeywords={values.newKeywords}
						onApply={async (v) => {
							setFieldValue('newKeywords', v, true);
							await validateForm();
							submitForm();
						}}
					/>
					<InputFieldWithLink
						label="Link"
						name="dropboxLink"
						value={values.dropboxLink?.trim() || ''}
						onApply={async (v: string | number) => {
							setFieldValue('dropboxLink', v, true);
							await validateForm();
							if (!errors.dropboxLink) submitForm();
						}}
						error={errors.dropboxLink}
						placeholder="https://..."
					/>
					<FilterPicker
						label="Filter"
						selectedValue={values.filter || ''}
						onApply={async (v) => {
							setFieldValue('filter', v, true);
							await validateForm();
							submitForm();
						}}
					/>
				</Form>
			)}
		</Formik>
	);
};
